<template>
  <div class="tw-p-2 tw-space-y-6">
    <!-- Header -->
    <div class="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
      <div>
        <h1 class="tw-text-2xl tw-font-bold tw-text-primary-900">{{ $t('navigation.foodshow-on-demand.title', 'FoodEshow on demand') }}</h1>
        <p class="tw-text-sm tw-text-gray-500">{{ $t('videos.library-subtitle', 'Watch on-demand video content') }}</p>
      </div>
      <div class="tw-min-w-[280px]">
        <FormField
          v-model="search"
          type="text"
          :hide-label="true"
          :hide-errors="true"
          :placeholder="$t('videos.search-placeholder', 'Search videos...')"
          left-icon="solar:magnifer-linear"
          @input="onSearch"
          class="tw-bg-white tw-rounded-md"
        />
      </div>
    </div>

    <!-- Category chips -->
    <div v-if="categories.length" class="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
      <button
        @click="selectCategory('')"
        :class="chipClass('')"
      >{{ $t('common.all', 'All') }}</button>
      <button
        v-for="c in categories"
        :key="c.id"
        @click="selectCategory(c.id)"
        :class="chipClass(c.id)"
      >{{ c.name }}</button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-5">
      <div v-for="n in 8" :key="n" class="tw-animate-pulse">
        <div class="tw-w-full tw-aspect-video tw-bg-gray-200 tw-rounded-lg tw-mb-2"></div>
        <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-3/4"></div>
      </div>
    </div>

    <!-- Empty -->
    <div v-else-if="!videos.length" class="tw-py-20 tw-text-center tw-text-gray-400">
      <icon icon="solar:videocamera-record-broken-bold-duotone" class="tw-text-6xl tw-mb-3" />
      <p>{{ $t('videos.empty-public', 'No videos available yet.') }}</p>
    </div>

    <!-- Grid -->
    <div v-else class="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-5">
      <router-link
        v-for="video in videos"
        :key="video.id"
        :to="{ name: 'shared.foodshow-on-demand.watch', params: { id: video.id } }"
        class="tw-group tw-block tw-bg-white tw-rounded-lg tw-overflow-hidden tw-border tw-border-gray-100 hover:tw-shadow-lg tw-transition-shadow"
      >
        <div class="tw-relative tw-w-full tw-aspect-video tw-bg-gray-100 tw-overflow-hidden">
          <img v-if="video.thumbnail_url" :src="video.thumbnail_url" class="tw-w-full tw-h-full tw-object-cover group-hover:tw-scale-105 tw-transition-transform" alt="" />
          <div v-else class="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
            <icon icon="solar:videocamera-record-bold-duotone" class="tw-text-4xl tw-text-gray-300" />
          </div>
          <div class="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black/0 group-hover:tw-bg-black/20 tw-transition-colors">
            <icon icon="solar:play-circle-bold" class="tw-text-5xl tw-text-white tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity" />
          </div>
          <span v-if="video.duration" class="tw-absolute tw-bottom-2 tw-right-2 tw-bg-black/70 tw-text-white tw-text-xs tw-px-1.5 tw-py-0.5 tw-rounded">{{ video.duration }}</span>
        </div>
        <div class="tw-p-3">
          <p class="tw-font-semibold tw-text-gray-800 tw-line-clamp-2 group-hover:tw-text-primary-600">{{ video.title }}</p>
          <div class="tw-flex tw-flex-wrap tw-gap-1 tw-mt-2">
            <span v-for="c in (video.categories || []).slice(0,2)" :key="c.id" class="tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-gray-100 tw-text-gray-500 tw-text-[11px]">{{ c.name }}</span>
          </div>
        </div>
      </router-link>
    </div>

    <!-- Load more -->
    <div v-if="!loading && pagination.current_page < pagination.last_page" class="tw-flex tw-justify-center tw-pt-2">
      <a-button intent="white" :loading="loadingMore" @click="loadMore" left-icon="solar:refresh-linear">
        {{ $t('common.load-more', 'Load more') }}
      </a-button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField.vue';
import videosApi from '@/api/videos';
import appApi from '@/api/app';
import useAlert from '@/composables/useAlert';

const { t } = useI18n();

const loading = ref(false);
const loadingMore = ref(false);
const videos = ref([]);
const categories = ref([]);
const search = ref('');
const activeCategory = ref('');

const pagination = reactive({ current_page: 1, last_page: 1, per_page: 12 });

let searchTimeout = null;

const chipClass = (id) => [
    'tw-px-3 tw-py-1.5 tw-rounded-full tw-text-sm tw-font-medium tw-transition-colors tw-border',
    activeCategory.value === id
        ? 'tw-bg-primary-500 tw-text-white tw-border-primary-500'
        : 'tw-bg-white tw-text-gray-600 tw-border-gray-200 hover:tw-bg-gray-50'
];

const buildParams = (page) => {
    const params = { page, per_page: pagination.per_page, search: search.value, category_id: activeCategory.value };
    Object.keys(params).forEach(k => { if (params[k] === '' || params[k] == null) delete params[k]; });
    return params;
};

const fetchVideos = async (page = 1, append = false) => {
    if (append) loadingMore.value = true; else loading.value = true;
    try {
        const response = await videosApi.paginate(buildParams(page));
        const data = response.data?.data;
        if (data && Array.isArray(data.data)) {
            videos.value = append ? [...videos.value, ...data.data] : data.data;
            pagination.current_page = data.current_page || 1;
            pagination.last_page = data.last_page || 1;
        } else if (!append) {
            videos.value = [];
        }
    } catch (error) {
        console.error('Error fetching videos:', error);
        useAlert(t('errors.something-went-wrong', 'Something went wrong'), 'danger');
    } finally {
        loading.value = false;
        loadingMore.value = false;
    }
};

const fetchCategories = async () => {
    try {
        const response = await appApi.forms.categories('video');
        categories.value = response.data || [];
    } catch (error) {
        console.error('Error fetching categories:', error);
    }
};

const onSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => fetchVideos(1), 500);
};

const selectCategory = (id) => {
    activeCategory.value = id;
    fetchVideos(1);
};

const loadMore = () => fetchVideos(pagination.current_page + 1, true);

onMounted(() => {
    fetchVideos();
    fetchCategories();
});
</script>

<style scoped>
</style>
